import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';

export default Vue.component('flexbox', {
  template: require('./template.html'),
  name: 'flexbox',
  computed: {
    ...mapGetters(['viewport', 'activeDroplet', 'activeDropletItem']),
    allColumns() {
      let list = [];
      const droplet = this.activeDroplet;
      if (droplet.rows) {
        _.forEach(this.activeDroplet.rows,
          row => _.forEach(row.columns, column => list.push(column)));
      }
      if (droplet.cards) {
        list = list.concat(droplet.cards);
      }
      if (droplet.columns) {
        list = list.concat(droplet.columns);
      }
      return { list };
    },
    hasAreas() {
      return Object.keys(this.areas).length > 0;
    },
    areaChecked() {
      return this.hasAreas && !this.areas.all && Object.values(this.areas).includes(true);
    },
  },
  props: ['option', 'active-state', 'dom-nodes', 'component-config'],
  data() {
    return {
      display: {
        'flex-direction': [
          'row',
          'row-reverse',
          'column',
          'column-reverse',
          'initial',
        ],
        'flex-wrap': [
          'nowrap',
          'wrap',
          'wrap-reverse',
          'initial',
        ],
        'justify-content': [
          'flex-start',
          'flex-end',
          'center',
          'space-between',
          'space-around',
          'normal',
        ],
        'align-items': [
          'flex-start',
          'flex-end',
          'center',
          'baseline',
          'stretch',
          'normal',
        ],
        'align-content': [
          'flex-start',
          'flex-end',
          'center',
          'space-between',
          'space-around',
          'normal',
        ],
      },
      models: {
        'flex-direction': {
          desktop: 'normal',
          tablet: 'normal',
          mobile: 'normal',
        },
        'flex-wrap': {
          desktop: 'normal',
          tablet: 'normal',
          mobile: 'normal',
        },
        'justify-content': {
          desktop: 'normal',
          tablet: 'normal',
          mobile: 'normal',
        },
        'align-items': {
          desktop: 'normal',
          tablet: 'normal',
          mobile: 'normal',
        },
        'align-content': {
          desktop: 'normal',
          tablet: 'normal',
          mobile: 'normal',
        },
      },
      tooltips: {
        'flex-direction': 'Direction of the flex containers main axis.',
        'flex-wrap': 'Controls whether the flex container is single or multi linem, and the direction of the cross axis.',
        'justify-content': 'Align flex items along the main axis of the current line of the flex container.',
        'align-items': 'Align flex items along the cross axis of the current line of the flex container.',
        'align-content': 'This helps to align a flex container\'s lines within it when there is extra space in the cross-axis, similar to how justify-content aligns individual items within the main-axis.',
      },
      debTime: 250,
      innerDomNodes: {},
      areas: {},
    };
  },
  watch: {
    viewport() {
      this.setInitialValue();
    },
    activeState() {
      this.setInitialValue();
    },
    activeDropletItem: {
      handler() {
        this.setInitialValue();
      },
      deep: true,
    },
  },
  created() {
    if (this.option.payload.areas) {
      _.forOwn(this.option.payload.areas, (area) => {
        this.$set(this.areas, area.property, area.property === 'all');
      });
    }
    // then check for previous values
    this.setInitialValue();
  },
  methods: {
    setInitialValue() {
      const option = this.option;
      let rule = option.payload.updates[0];
      const first = rule;
      _.forOwn(this.display, (value, property) => {
        let groupParent;
        let selector;
        // if one area is checked, get that rule from the updates list
        if (this.areaChecked) {
          const checked = _.keys(_.pickBy(this.areas));
          rule = _.find(option.payload.updates, update => checked.includes(update.property));
        }
        if (this.activeDropletItem._id && this.componentConfig.itemList) {
          groupParent = first;
          const inner = this.componentConfig.itemList.selectors[property];
          const nested = inner || '';
          selector = `#drp-${this.activeDropletItem._id}${nested}${this.activeState}`;
        } else {
          selector = rule.selector.replace(/\$\{state\}/gi, this.activeState);
        }
        // if one area is checked, get that rule from the updates list
        const useRoot = option.$el === 'root';
        const $root = this.domNodes.$dropletRow;
        const $el = useRoot ? $root : $root.querySelectorAll(option.$el)[0];
        // defaults are listed last in each option array
        const defaultVal = value[value.length - 1];
        this.models[property][this.viewport] = this._getPropertyRule({
          $vm: this,
          default: defaultVal,
          $el,
          selector,
          property,
          groupParent,
        });
      });
    },
    onChange(params, property) {
      const value = this.models[property][this.viewport];
      const payload = {};
      let updates = params.updates;
      payload.drp = this.activeDroplet;
      payload.val = value;
      // if there is at least one area checked besides all
      if (this.hasAreas && this.areaChecked) {
        const checked = _.keys(_.pickBy(this.areas));
        updates = _.filter(updates, update => checked.includes(update.property));
      }
      payload.viewport = this.viewport;
      _.forEach(updates, (item) => {
        let selector;
        if (item.value) {
          payload.val = item.value;
        }
        if (this.activeDropletItem._id && this.componentConfig.itemList) {
          // this is in the event a user is targeting a specific item within a droplet
          // eg, a nav link or a column in columns
          const nested = this.componentConfig.itemList.selectors[item.property || property] || '';
          const defaultSel = `#drp-${this.activeDropletItem._id}${nested}`;
          selector = `${defaultSel}${this.activeState}`;
        } else {
          this.resetCustomQueries(item);
          selector = item.selector.replace(/\$\{state\}/gi, this.activeState);
          selector = selector.replace(/\$\{dropletId\}/gi, this.activeDroplet._id);
        }
        payload.property = item.property || property;
        payload.selector = selector;
        payload.noId = item.noId;
        this.$emit('check-legacy-styles', payload);
        this.$store.dispatch('changeDropletCss', payload);
        // always reset the value
        payload.val = value;
      });
      this.$emit('css-generator', this.cssPayload(), this.option.plugin);
      this.$emit('update-changes', true);
    },
    resetCustomQueries(update) {
      if (!this.componentConfig.itemList) {
        return;
      }
      const inner = this.componentConfig.itemList.selectors[update.property];
      const nested = inner || '';
      const customRule = new RegExp(`#drp-[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}${nested}`, 'gi');
      _.forEach(this.activeDroplet.mediaQueries, (rule) => {
        if (rule.selector.match(customRule)) {
          const property = _.find(rule.properties, r => r.property === update.property);
          if (property) {
            this.$store.dispatch('setDropletVal', {
              drp: property,
              key: this.viewport,
              val: 'default',
            });
          }
        }
      });
    },
    checkArea(area) {
      _.forOwn(this.areas, (value, key) => {
        if (area !== key) {
          this.areas[key] = false;
        }
      });
      if ((area === 'all' && !this.areas.all) || (area !== 'all' && !this.areaChecked)) {
        this.areas.all = true;
      }
      this.setInitialValue();
    },
    cssPayload() {
      let payload = false;
      if (this.option.payload.useColumns) {
        payload = {
          key: 'drps',
          value: [...this.allColumns.list, this.activeDroplet],
        };
      }
      return payload;
    },
    startCase(string) {
      return _.startCase(string);
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/page/style-sidebar/options/flexbox/index.js